import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Building2, Mail, Phone, MapPin, Save, RefreshCw, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SuperAdminThemeSettings from '../components/SuperAdminThemeSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompanyInfo = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [showTestEmailDialog, setShowTestEmailDialog] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    address: '',
    city: '',
    province: '',
    postal_code: '',
    phone: '',
    email: '',
    whatsapp: '',
    website: '',
    logo_url: '',
    // WhatsApp Gateway (WAblas)
    wablas_enabled: false,
    wablas_token: '',
    wablas_domain: '',
    wablas_sender: '',
    // SMTP Configuration
    smtp_enabled: false,
    smtp_host: '',
    smtp_port: '587',
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    smtp_secure: false
  });

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      // Use V2 MySQL endpoint
      const response = await axios.get(`${API_URL}/api/v2/superadmin/company-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.data) {
        setFormData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
      if (error.response?.status !== 404) {
        toast.error('Gagal memuat data perusahaan');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Ukuran file terlalu besar. Max 2MB');
      return;
    }

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Format file tidak valid. Gunakan PNG, JPG, atau SVG');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({
        ...prev,
        logo_url: reader.result
      }));
      toast.success('Logo berhasil diupload!');
    };
    reader.onerror = () => {
      toast.error('Gagal membaca file');
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.company_name) {
      toast.error('Nama perusahaan wajib diisi');
      return;
    }
    if (!formData.email) {
      toast.error('Email wajib diisi');
      return;
    }
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      // Use V2 MySQL endpoint
      const response = await axios.put(
        `${API_URL}/api/v2/superadmin/company-info`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        toast.success('Data perusahaan berhasil disimpan!');
      }
    } catch (error) {
      console.error('Error saving company info:', error);
      const errorMsg = error.response?.data?.detail || 'Gagal menyimpan data';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    // Validate SMTP fields
    if (!formData.smtp_host || !formData.smtp_port || !formData.smtp_username || !formData.smtp_password) {
      toast.error('Lengkapi semua field SMTP terlebih dahulu');
      return;
    }
    
    if (!testEmailAddress) {
      toast.error('Masukkan email tujuan untuk test');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmailAddress)) {
      toast.error('Format email tidak valid');
      return;
    }
    
    try {
      setTestingSmtp(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_URL}/api/v2/superadmin/test-smtp`,
        {
          smtp_host: formData.smtp_host,
          smtp_port: formData.smtp_port,
          smtp_username: formData.smtp_username,
          smtp_password: formData.smtp_password,
          smtp_from_email: formData.smtp_from_email,
          smtp_from_name: formData.smtp_from_name,
          smtp_secure: formData.smtp_secure,
          test_email: testEmailAddress
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
        setShowTestEmailDialog(false);
        setTestEmailAddress('');
      }
    } catch (error) {
      console.error('Test SMTP error:', error);
      const errorMsg = error.response?.data?.detail || 'Gagal mengirim test email';
      toast.error(errorMsg);
    } finally {
      setTestingSmtp(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Building2 className="w-8 h-8 text-purple-600 dark:text-purple-400" />
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
            Company Information
          </h1>
        </div>
        <p className="text-slate-600 dark:text-slate-400">
          Kelola informasi perusahaan yang akan digunakan pada invoice dan dokumen lainnya
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Informasi Perusahaan</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Nama Perusahaan <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="RadiusLink"
                required
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Logo Upload */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Logo Perusahaan
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  onChange={handleLogoUpload}
                  className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 cursor-pointer dark:text-slate-400"
                />
              </div>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Format: PNG, JPG, SVG. Max size: 2MB. Recommended: 400x100px
              </p>
              {formData.logo_url && (
                <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-slate-600 dark:text-slate-400">Preview:</p>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, logo_url: ''})}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Hapus Logo
                    </button>
                  </div>
                  <img 
                    src={formData.logo_url} 
                    alt="Logo preview" 
                    className="h-20 object-contain bg-white p-2 rounded dark:bg-slate-800"
                  />
                </div>
              )}
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Alamat Lengkap
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Jl. Contoh No. 123"
                rows="3"
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* City, Province, Postal */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Kota
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Jakarta"
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Provinsi
                </label>
                <input
                  type="text"
                  name="province"
                  value={formData.province}
                  onChange={handleChange}
                  placeholder="DKI Jakarta"
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Kode Pos
                </label>
                <input
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  placeholder="12345"
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* WhatsApp Gateway */}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <Phone className="w-5 h-5 text-green-600 dark:text-green-400" />
                WhatsApp Gateway (WAblas)
              </h3>
              
              <div className="space-y-4">
                {/* Enable WAblas */}
                <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <input
                    type="checkbox"
                    name="wablas_enabled"
                    checked={formData.wablas_enabled}
                    onChange={(e) => setFormData(prev => ({...prev, wablas_enabled: e.target.checked}))}
                    className="w-4 h-4 text-purple-600 border-slate-300 rounded focus:ring-purple-500 dark:text-purple-400 dark:border-slate-600"
                  />
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Aktifkan WhatsApp Gateway (WAblas)
                  </label>
                </div>

                {formData.wablas_enabled && (
                  <>
                    {/* WAblas Token */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        WAblas Token/API Key
                      </label>
                      <input
                        type="text"
                        name="wablas_token"
                        value={formData.wablas_token}
                        onChange={handleChange}
                        placeholder="Masukkan token dari dashboard WAblas"
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                      />
                    </div>

                    {/* WAblas Domain */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        WAblas Domain
                      </label>
                      <input
                        type="text"
                        name="wablas_domain"
                        value={formData.wablas_domain}
                        onChange={handleChange}
                        placeholder="pati.wablas.com atau custom domain"
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Contoh: pati.wablas.com, solo.wablas.com, atau domain custom Anda
                      </p>
                    </div>

                    {/* Sender Number */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Nomor Pengirim WA
                      </label>
                      <input
                        type="text"
                        name="wablas_sender"
                        value={formData.wablas_sender}
                        onChange={handleChange}
                        placeholder="628123456789"
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Format: 628xxx (tanpa + atau spasi)
                      </p>
                    </div>

                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                      <p className="text-sm text-green-800 dark:text-green-200">
                        💡 <strong>Info:</strong> WAblas akan digunakan untuk mengirim notifikasi invoice, payment reminder, dan pesan lainnya ke tenant.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* SMTP Configuration */}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Email SMTP Configuration
              </h3>
              
              <div className="space-y-4">
                {/* Enable SMTP */}
                <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <input
                    type="checkbox"
                    name="smtp_enabled"
                    checked={formData.smtp_enabled}
                    onChange={(e) => setFormData(prev => ({...prev, smtp_enabled: e.target.checked}))}
                    className="w-4 h-4 text-purple-600 border-slate-300 rounded focus:ring-purple-500 dark:text-purple-400 dark:border-slate-600"
                  />
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Aktifkan SMTP Email Service
                  </label>
                </div>

                {formData.smtp_enabled && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* SMTP Host */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          SMTP Host
                        </label>
                        <input
                          type="text"
                          name="smtp_host"
                          value={formData.smtp_host}
                          onChange={handleChange}
                          placeholder="smtp.gmail.com"
                          className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>

                      {/* SMTP Port */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          SMTP Port
                        </label>
                        <input
                          type="text"
                          name="smtp_port"
                          value={formData.smtp_port}
                          onChange={handleChange}
                          placeholder="587"
                          className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          Port 587 (TLS) atau 465 (SSL)
                        </p>
                      </div>
                    </div>

                    {/* SMTP Username */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        SMTP Username
                      </label>
                      <input
                        type="text"
                        name="smtp_username"
                        value={formData.smtp_username}
                        onChange={handleChange}
                        placeholder="your-email@gmail.com"
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>

                    {/* SMTP Password */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        SMTP Password / App Password
                      </label>
                      <input
                        type="password"
                        name="smtp_password"
                        value={formData.smtp_password}
                        onChange={handleChange}
                        placeholder="••••••••••••••••"
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Untuk Gmail, gunakan App Password (bukan password akun)
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* From Email */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          From Email
                        </label>
                        <input
                          type="email"
                          name="smtp_from_email"
                          value={formData.smtp_from_email}
                          onChange={handleChange}
                          placeholder="noreply@radiuslink.id"
                          className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>

                      {/* From Name */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          From Name
                        </label>
                        <input
                          type="text"
                          name="smtp_from_name"
                          value={formData.smtp_from_name}
                          onChange={handleChange}
                          placeholder="RadiusLink"
                          className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* SSL/TLS */}
                    <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <input
                        type="checkbox"
                        name="smtp_secure"
                        checked={formData.smtp_secure}
                        onChange={(e) => setFormData(prev => ({...prev, smtp_secure: e.target.checked}))}
                        className="w-4 h-4 text-purple-600 border-slate-300 rounded focus:ring-purple-500 dark:text-purple-400 dark:border-slate-600"
                      />
                      <div>
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Gunakan SSL/TLS
                        </label>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Centang jika menggunakan port 465 (SSL)
                        </p>
                      </div>
                    </div>

                    {/* Test Email Section */}
                    <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-800">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                            <Send className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            Test Email SMTP
                          </h4>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                            Kirim email test untuk memverifikasi konfigurasi SMTP
                          </p>
                        </div>
                      </div>
                      
                      {showTestEmailDialog ? (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                              Email Tujuan Test
                            </label>
                            <input
                              type="email"
                              value={testEmailAddress}
                              onChange={(e) => setTestEmailAddress(e.target.value)}
                              placeholder="admin@example.com"
                              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              onClick={handleTestSmtp}
                              disabled={testingSmtp || !testEmailAddress}
                              className="flex-1 bg-blue-600 hover:bg-blue-700"
                            >
                              {testingSmtp ? (
                                <>
                                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                  Mengirim...
                                </>
                              ) : (
                                <>
                                  <Send className="w-4 h-4 mr-2" />
                                  Kirim Test Email
                                </>
                              )}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setShowTestEmailDialog(false);
                                setTestEmailAddress('');
                              }}
                              disabled={testingSmtp}
                            >
                              Batal
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowTestEmailDialog(true)}
                          className="w-full border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20"
                          disabled={!formData.smtp_host || !formData.smtp_username || !formData.smtp_password}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Test Kirim Email
                        </Button>
                      )}
                      
                      {(!formData.smtp_host || !formData.smtp_username || !formData.smtp_password) && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                          ⚠️ Lengkapi SMTP Host, Username & Password terlebih dahulu
                        </p>
                      )}
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
                        💡 <strong>Info:</strong> SMTP akan digunakan untuk mengirim:
                      </p>
                      <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 ml-4">
                        <li>• Invoice email ke tenant</li>
                        <li>• Payment confirmation</li>
                        <li>• Reminder & notifications</li>
                      </ul>
                    </div>

                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        ⚠️ <strong>Gmail Users:</strong> Gunakan App Password, bukan password akun biasa.
                        <br />
                        Generate di: Google Account → Security → 2-Step Verification → App passwords
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                Informasi Kontak
              </h3>
              
              <div className="space-y-4">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5 dark:text-slate-500" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="info@radiuslink.id"
                      required
                      className="w-full pl-11 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Telepon
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5 dark:text-slate-500" />
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="021-12345678"
                      className="w-full pl-11 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* WhatsApp */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    WhatsApp
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5 dark:text-slate-500" />
                    <input
                      type="text"
                      name="whatsapp"
                      value={formData.whatsapp}
                      onChange={handleChange}
                      placeholder="08123456789"
                      className="w-full pl-11 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Format: 08xxx (tanpa +62 atau 62)
                  </p>
                </div>

                {/* Website */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://radiuslink.id"
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={saving}
                className="flex-1"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Menyimpan...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Simpan Perubahan
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={fetchCompanyInfo}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Reset
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>

      {/* Info Box */}
      <Card className="mt-6 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                Informasi ini akan digunakan untuk:
              </h4>
              <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                <li>• Header invoice tenant</li>
                <li>• Email notifikasi</li>
                <li>• Dokumen resmi lainnya</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Super Admin Theme Settings */}
      <div className="mt-6">
        <SuperAdminThemeSettings />
      </div>
    </div>
  );
};

export default CompanyInfo;
